import React, { useContext } from 'react';
import { connectHits } from 'react-instantsearch-dom';
import PropTypes from 'prop-types';

import SearchResultsEverything from '../SearchResultsEverything';
import SearchState from '../../elements/SearchState';
import SearchResultsForType from '../SearchResultsForType';

// members
import { SAVED_CONTACTS_RECORD_ID_QUERY } from '@general/graphql/contact.graph';
import { mapSearchMemberToProps } from '@general/transforms/member.transform';
import MemberListItem from '@molecules/MemberListItem';
import MemberCard from '@molecules/MemberCard';

// companies
import { SAVED_COMPANIES_QUERY } from '@general/graphql/company.graph';
import { mapSearchCompanyToProps } from '@general/transforms/company.transform';
import CompanyListItem from '@molecules/CompanyListItem';
import CompanyCard from '@molecules/CompanyCard';

// articles
import { SAVED_ARTICLES_QUERY } from '@general/graphql/article.graph';
import { mapSearchArticleToProps } from '@general/transforms/article.transform';
import ArticleCard from '@molecules/ArticleCard';
import ArticleListItem from '@molecules/ArticleListItem';

// publications
import { SAVED_PUBLICATIONS_QUERY } from '@general/graphql/publication.graph';
import { mapSearchPublicationToProps } from '@general/transforms/publication.transform';
import PublicationListItem from '@molecules/PublicationListItem';
import PublicationCard from '@molecules/PublicationCard';

// events
import { SAVED_EVENTS_QUERY } from '@general/graphql/event.graph';
import { mapSearchEventToProps } from '@general/transforms/event.transform';
import EventListItem from '@molecules/EventListItem';
import EventCard from '@molecules/EventCard';

// Functionality
import { SearchTypeContext } from '../../../context/searchTypeContext';
import useAuthorized from '@hooks/useAuthorized';
import useIcscTracking from '@tracking/useIcscTracking';

const SearchResultsComponent = ({ hits }) => {
    const [searchType] = useContext(SearchTypeContext);
    const { isLoggedIn } = useAuthorized();
    const { trackEvent } = useIcscTracking();

    if (hits.length === 0) {
        return <SearchState hitCount={hits.length} />;
    }

    const handleGenericClick = ({ hit, url, index }) => {
        trackEvent({
            click: {
                index,
                hit,
                target: 'click_search_hit',
                value: 'Click Search Result',
            },
        });
    };

    if (searchType.index === 'everything') {
        return <SearchResultsEverything hits={hits} onClick={handleGenericClick} isLoggedIn={isLoggedIn} />;
    }

    if (searchType.index === 'members' || searchType.index === 'member_events') {
        return (
            <SearchResultsForType
                hits={hits}
                typePlural="contacts"
                query={SAVED_CONTACTS_RECORD_ID_QUERY}
                resultsPerRow={3}
                ListItemComponent={MemberListItem}
                CardComponent={MemberCard}
                transformFunc={mapSearchMemberToProps}
                onClick={handleGenericClick}
                isLoggedIn={isLoggedIn}
            />
        );
    }

    if (searchType.index === 'companies') {
        return (
            <SearchResultsForType
                hits={hits}
                typePlural="companies"
                query={SAVED_COMPANIES_QUERY}
                ListItemComponent={CompanyListItem}
                CardComponent={CompanyCard}
                transformFunc={mapSearchCompanyToProps}
                onClick={handleGenericClick}
                isLoggedIn={isLoggedIn}
            />
        );
    }
    if (searchType.index === 'articles') {
        return (
            <SearchResultsForType
                hits={hits}
                typePlural="articles"
                query={SAVED_ARTICLES_QUERY}
                ListItemComponent={ArticleListItem}
                CardComponent={ArticleCard}
                transformFunc={mapSearchArticleToProps}
                onClick={handleGenericClick}
                isLoggedIn={isLoggedIn}
            />
        );
    }
    if (searchType.index === 'publications') {
        return (
            <SearchResultsForType
                hits={hits}
                typePlural="publications"
                query={SAVED_PUBLICATIONS_QUERY}
                ListItemComponent={PublicationListItem}
                CardComponent={PublicationCard}
                transformFunc={mapSearchPublicationToProps}
                onClick={handleGenericClick}
                isLoggedIn={isLoggedIn}
            />
        );
    }
    if (searchType.index === 'events') {
        return (
            <SearchResultsForType
                hits={hits}
                typePlural="viewerEvents"
                query={SAVED_EVENTS_QUERY}
                ListItemComponent={EventListItem}
                CardComponent={EventCard}
                transformFunc={mapSearchEventToProps}
                onClick={handleGenericClick}
                isLoggedIn={isLoggedIn}
            />
        );
    }

    return null;
};

SearchResultsComponent.propTypes = {
    hits: PropTypes.any,
};

const SearchResults = connectHits(SearchResultsComponent);
export default SearchResults;
